import { TableBody, TableCell } from '@material-ui/core';
import {
  Box,
  Card,
  Table,
  Dialog,
  DialogContent,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import React, { FC, useEffect, useState } from 'react';
import { useDataProvider, useRedirect } from 'react-admin';
import { Competition } from '../../types/competition';
import ReplyIcon from '@material-ui/icons/Reply';
import './index.css';
import { getDate } from '../../helpers/dateHelper';
import VisibilityIcon from '@material-ui/icons/Visibility';
import HeadTable from '../common/sortTable/sortTable';
import { title } from 'process';
import { getFieldByTitle } from '../../helpers/sortByTitle';
import PaginationTable from '../common/paginationTable';
import { getAmountTickets } from '../../helpers/competitioDetails';
import TicketsDetails from './TicketsDetails';
import { Order } from '../../types/order';

interface TitleProps {
  competition: Competition | undefined;
}

interface StateDetails {
  prizes: Array<any>;
  competition: undefined | Competition;
  total: number;
  page: number;
  perPage: number;
  field: string;
  order: string;
  isPrize: boolean;
  competitionType: string;
}

const getCompetitionType = (competition: string): string => {
  switch (competition) {
    case 'DREAMHOME':
      return 'raffle';
    case 'PRIZE':
      return 'prize';
    case 'FIXED_ODDS':
      return 'fixedOdds';
    case 'WEEKLY_SUBSCRIPTION_DRAW':
      return 'weeklySubscriptionDraw';
    default:
      return '';
  }
};

const CompetitionDetails: FC = (props: any) => {
  const dataProvider = useDataProvider();
  const [state, setState] = useState<StateDetails>({
    prizes: Array<any>(),
    competition: undefined,
    total: 0,
    page: 1,
    perPage: 10,
    field: '_id',
    order: 'desc',
    isPrize: false,
    competitionType: '',
  });

  const [modal, setModal] = useState({
    isOpen: false,
    prizeId: '',
    isActive: false,
  });

  useEffect(() => {
    getCompetitionDetails(1, 10, '_id', 'asc');
  }, []);

  const getCompetitionDetails = async (
    page: number,
    perPage: number,
    field: string,
    order: string
  ) => {
    const location = props.location.pathname.split('/');
    const id = location[location.length - 1];
    const result = (await dataProvider.getCompetitionDetails(
      'competitions/details',
      {
        filter: {
          id: id,
        },
        pagination: { page: page, perPage: perPage },
        sort: { field: field, order: order },
      }
    )) as any;
    setState({
      ...state,
      prizes: result.data.competitions,
      isPrize:
        result.data.competition.competitionType === 'PRIZE' ? true : false,
      competitionType: getCompetitionType(
        result.data.competition.competitionType
      ),
      competition: result.data.competition,
      page: page,
      perPage: perPage,
      field: field,
      order: order,
      total: result.data.allCount,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const perPage = parseInt(event.target.value, 10);
    getCompetitionDetails(1, perPage, state.field, state.order);
  };

  const handleLastPage = () => {
    const page = Math.ceil(state.total / state.perPage);
    getCompetitionDetails(page, state.perPage, state.field, state.order);
  };

  const handleFirstPage = () => {
    getCompetitionDetails(1, state.perPage, state.field, state.order);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    getCompetitionDetails(newPage + 1, state.perPage, state.field, state.order);
  };

  const handleSort = (order: string, title: string) => {
    let field = getFieldByTitle(title);
    if (field === 'title') {
      field = 'prizeName';
    }
    getCompetitionDetails(state.page, state.perPage, field, order);
  };

  const toShowTicketsDetails = (
    prizeId: string,
    isActive: boolean | undefined
  ) => {
    const active = isActive ? true : false;
    setModal({ ...modal, isOpen: true, prizeId: prizeId, isActive: active });
  };

  const closeModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  return (
    <>
      <DetailsListTitle competition={state.competition} />
      <Box marginTop='0.5em'>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell id='active-table-head'>
                    <HeadTable sort={handleSort} title='Title' />
                  </TableCell>
                  {state.isPrize && (
                    <>
                      <TableCell id='active-table-head'>
                        <HeadTable sort={handleSort} title='Category' />
                      </TableCell>
                      <TableCell id='active-table-head'>
                        <HeadTable sort={handleSort} title='Sub-Category' />
                      </TableCell>
                    </>
                  )}
                  <TableCell id='active-table-head'>
                    <HeadTable sort={handleSort} title='Tickets purchased' />
                  </TableCell>
                  <TableCell id='status-active-table-head'>Actions</TableCell>
                </TableRow>
              </TableHead>
              {state.prizes.map((item) => {
                const isDeleted = item.prizeName ? false : true;
                return (
                  <TableBody>
                    <TableCell>
                      {isDeleted ? (
                        <span className='prize-deleted'>Prize Deleted</span>
                      ) : (
                        item.prizeName
                      )}
                    </TableCell>
                    {state.isPrize && (
                      <>
                        <TableCell>
                          {item.category ? item.category.title : ''}
                        </TableCell>
                        <TableCell>
                          {item.subcategory ? item.subcategory.title : ''}
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      {item.orders ? item.orders.ticketsCount : 0}
                    </TableCell>
                    <TableCell>
                      <Box display='flex' justifyContent='center'>
                        {!isDeleted && (
                          <VisibilityIcon
                            onClick={() =>
                              toShowTicketsDetails(
                                item.prizeId,
                                state.competition?.isActive
                              )
                            }
                            id='show-tickets'
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableBody>
                );
              })}
            </Table>
          </TableContainer>
          <PaginationTable
            page={state.page}
            perPage={state.perPage}
            collectionLength={state.prizes.length}
            total={state.total}
            handleChangePage={handleChangePage}
            handleLastPage={handleLastPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleFirstPage={handleFirstPage}
            title='Prizes'
          />
        </Card>
        <Dialog fullWidth open={modal.isOpen} onClose={closeModal}>
          <DialogContent>
            <TicketsDetails
              competitionId={state.competition ? state.competition._id : ''}
              prize={modal.prizeId}
              isActive={modal.isActive}
              competitionType={state.competitionType}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </>
  );
};

const DetailsListTitle: FC<TitleProps> = ({ competition }) => {
  const redirect = useRedirect();
  const toList = () => {
    redirect('/competitions');
  };
  return (
    <Box marginTop='2em'>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <div className='active-prizes-container'>
          Competition #{competition ? competition.competitionId : ''}
        </div>
        <div className='icon-back'>
          <ReplyIcon onClick={toList} />
        </div>
      </Box>
      <Box
        display='flex'
        justifyContent='space-between'
        marginLeft='4em'
        alignItems='baseline'
      >
        <div>
          <span className='details-status status-bold'>Status</span>
          <span className='details-status'>
            {competition
              ? competition.isActive
                ? ' Active'
                : ' Completed'
              : ''}
          </span>
        </div>
        <div>
          <Box
            display='flex'
            alignItems='center'
            className='active-prizes-container'
          >
            <Box marginBottom='1em'>
              <Box display='flex'>
                <div className='prize-date status-bold'>Start date</div>
                <div className='prize-date'>
                  {competition ? getDate(new Date(competition.startAt)) : ''}
                </div>
              </Box>
              <Box display='flex'>
                <div className='prize-date status-bold'>Finish date</div>
                <div className='prize-date'>
                  {competition ? getDate(new Date(competition.endsAt)) : ''}
                </div>
              </Box>
            </Box>
          </Box>
        </div>
        <div></div>
      </Box>
    </Box>
  );
};

export default CompetitionDetails;
